import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  LocationOn as LocationIcon,
  Add as AddIcon,
  SearchOff as SearchOffIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material';

/**
 * Empty state component for location views
 * @param {string} type - 'no_locations' | 'no_results' | 'no_selection' | 'error'
 * @param {Function} onAction - Action button click handler
 * @param {boolean} canCreate - Whether user can create locations
 * @param {string} errorMessage - Error message to display for 'error' type
 * @param {Function} t - Translation function
 */
const LocationEmptyState = ({ type = 'no_locations', onAction, canCreate = false, errorMessage, t }) => {
  const configs = {
    no_locations: {
      icon: <LocationIcon sx={{ fontSize: 64, color: 'text.disabled' }} />,
      title: t ? t('LocationManagement.no_locations') : 'No Locations',
      description: t ? t('LocationManagement.no_locations_desc') : 'No locations have been created yet. Add your first location to get started.',
      actionLabel: t ? t('LocationManagement.add_location') : 'Add Location',
      showAction: canCreate
    },
    no_results: {
      icon: <SearchOffIcon sx={{ fontSize: 64, color: 'text.disabled' }} />,
      title: t ? t('LocationManagement.no_results') : 'No Results',
      description: t ? t('LocationManagement.no_results_desc') : 'No locations match your current filters. Try adjusting your search or filter criteria.',
      actionLabel: t ? t('LocationManagement.clear_filters') : 'Clear Filters',
      showAction: true
    },
    no_selection: {
      icon: <LocationIcon sx={{ fontSize: 64, color: 'text.disabled' }} />,
      title: t ? t('LocationManagement.select_location') : 'Select a Location',
      description: t ? t('LocationManagement.choose_location_from_tree_view') : 'Choose a location from the tree view to see details and manage devices',
      showAction: false
    },
    error: {
      icon: <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />,
      title: t ? t('LocationManagement.error_loading') : 'Error Loading Locations',
      description: errorMessage || (t ? t('LocationManagement.error_loading_desc') : 'Something went wrong while loading locations. Please try again.'),
      actionLabel: t ? t('LocationManagement.retry') : 'Retry',
      showAction: true
    }
  };

  const config = configs[type] || configs.no_locations;

  return (
    <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
      {config.icon}
      <Typography variant="h5" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
        {config.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
        {config.description}
      </Typography>
      {config.showAction && onAction && (
        <Button
          variant="contained"
          startIcon={type === 'no_locations' ? <AddIcon /> : undefined}
          onClick={onAction}
        >
          {config.actionLabel}
        </Button>
      )}
    </Box>
  );
};

export default LocationEmptyState;
